// Components/PaymentPriceCard.jsx
import React, { useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { Heading } from '@/components/ui/shared/heading';
import { Separator } from '@/components/ui/separator';
import { useCartData } from '../../context/CartDataContext';
import { useRegistration } from '../../context/RegistrationContext';
import { useUserState } from '@/components/context/UserContext';
import RedeemCodeSection from '@/v2/components/RedeemCodeSection';
import useFetch from 'use-http';
import { CircleAlert, X } from 'lucide-react';
import {
  IsIncorrectChild,
  priceForServiceBeforeDiscount,
} from '@/v2/utils/priceUtils';
import { DiscountApplicationStatus } from '@/v2/constants/discountApplicationStatus';
import {
  is3TWorkplace,
  isMrt,
} from '@/v2/utils/commonServiceClassificationRulesUtils';

const formatPrice = (eur) =>
  new Intl.NumberFormat('lt-LT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(eur) || 0);

function parseIsoOrNull(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function normalizeCouponResponse(res) {
  if (!res || typeof res !== 'object') {
    return { found: false, dateOk: false, balance: null };
  }
  const statusOk =
    typeof res.statusId === 'string' &&
    res.statusId.toLowerCase() === 'galioja';

  const found =
    res.isCouponFound === true ||
    res.isValid === true ||
    res.isPass === true ||
    statusOk;

  if (res.isApplicableByDate === false) {
    return { found, dateOk: false, balance: res.balance ?? null };
  }

  const validTo = parseIsoOrNull(res.validTo);
  const now = new Date();
  const dateOk = validTo ? validTo.getTime() >= now.getTime() : true;

  return { found, dateOk, balance: res.balance ?? null };
}

export default function PaymentPriceCard() {
  const { consultationInfo, paymentInfo, userInfo, setPaymentInfo } =
    useRegistration();
  const userState = useUserState();
  const isDiscountCard = userState?.discountCard;
  const [showAdditionalLabServices, setShowAdditionalLabServices] =
    React.useState(false);

  const {
    servicesData,
    service,
    services,
    selectedTimeSlot,
    additionalServices = [],
    setServices,
  } = useCartData?.() || {};
  const GSKServiceTypeExternalId =
    process.env.REACT_APP_GSK_SERVICE_TYPE_EXT_ID;
  const VGSKServiceTypeExternalId =
    process.env.REACT_APP_VGSK_SERVICE_TYPE_EXT_ID;
  const svc = React.useMemo(() => {
    if (service && typeof service === 'object') return service;
    if (!servicesData) return undefined;
    return Array.isArray(servicesData) ? servicesData[0] : servicesData;
  }, [service, servicesData]);

  const additionalSvc = React.useMemo(() => {
    if (!services) return undefined;
    if (Array.isArray(services) && services.length > 1) {
      setShowAdditionalLabServices(true);
    }
    return Array.isArray(services) ? services.slice(1) : service;
  }, [service, services]);

  const isPaid = !!selectedTimeSlot?.isPaid;
  const isExtra = !!selectedTimeSlot?.isExtra;

  const priced = paymentInfo?.servicePrice || {};

  const isPsdfMode = !isPaid;
  const isExtraMode = !isPsdfMode && isExtra;
  const isNormalMode = !isPsdfMode && !isExtraMode;

  const showSecondary = isNormalMode && svc?.secondaryPrice != null;

  const primaryLabel = 'Pirminė konsultacija';
  const secondaryLabel =
    'Pakartotinė konsultacija dėl tos pačios priežasties 1 mėn. laikotarpyje';

  const basePrimary = Number(svc?.priceWithDiscount);
  const baseSecondary = Number(
    isDiscountCard ? svc?.secondaryDiscountEur : svc?.secondaryPrice,
  );

  const visitTypeString = showSecondary
    ? consultationInfo?.visitType || 'primary'
    : 'primary';

  const baseMainPrice = priceForServiceBeforeDiscount({
    svc,
    visitType: visitTypeString,
    isExtraSlot: isExtra,
    psdf: isPsdfMode,
  });

  const usingSecondary =
    isNormalMode &&
    visitTypeString === 'secondary' &&
    Number.isFinite(baseSecondary);

  const visitTypeValue = isExtraMode ? 3 : usingSecondary ? 2 : 1;

  const normalTitle = service?.serviceTitle;
  const normalBase = usingSecondary
    ? Number.isFinite(baseSecondary)
      ? baseSecondary
      : 0
    : Number.isFinite(basePrimary)
      ? basePrimary
      : 0;

  const normalServerTotal =
    priced?.PriceType === visitTypeValue && priced?.TotalPrice != null
      ? Number(priced.TotalPrice)
      : null;

  const normalLinePrice =
    normalServerTotal != null ? normalServerTotal : normalBase;

  const extraTitle = 'Papildoma paslauga';
  const baseExtra = Number(
    (svc && (svc.extraPrice ?? svc.priceWithDiscount)) ?? 0,
  );
  const extraServerTotal =
    priced?.PriceType === 3 && priced?.TotalPrice != null
      ? Number(priced.TotalPrice)
      : null;
  const extraLinePrice =
    extraServerTotal != null
      ? extraServerTotal
      : Number.isFinite(baseExtra)
        ? baseExtra
        : 0;

  const psdfTitle = 'Paslauga PSDF lėšomis';
  const psdfLinePrice = 0;

  const title = isPsdfMode ? psdfTitle : isExtraMode ? extraTitle : normalTitle;
  const linePrice = isPsdfMode
    ? psdfLinePrice
    : isExtraMode
      ? extraLinePrice
      : normalLinePrice;

  // ---- BASE TOTALS (before discounts, coupons, repricing) ----

  // Mandatory additional services base (always raw price)
  const baseMandatoryAdditionals = Array.isArray(additionalServices)
    ? additionalServices.reduce((sum, s) => sum + (Number(s?.price) || 0), 0)
    : 0;

  // Additional LAB services base (same logic as main price)
  const baseLabAdditionals = Array.isArray(additionalSvc)
    ? additionalSvc.reduce((sum, s) => {
        const base = priceForServiceBeforeDiscount({
          svc: s,
          visitType: 'primary',
          isExtraSlot: false,
          psdf: isPsdfMode,
        });
        return sum + (Number(base) || 0);
      }, 0)
    : 0;

  // Full base price before any discounts/coupons/repricing
  const baseTotalPrice =
    (Number(baseMainPrice) || 0) +
    Number(baseMandatoryAdditionals) +
    Number(baseLabAdditionals);

  const additionalsTotal = Array.isArray(additionalServices)
    ? additionalServices.reduce(
        (sum, a) => sum + (Number(a?.priceWithDiscount) || 0),
        0,
      )
    : 0;

  const additionalLabServiceTotal = Array.isArray(additionalSvc)
    ? additionalSvc.reduce(
        (sum, a) => sum + (Number(a?.priceWithDiscount) || 0),
        0,
      )
    : 0;

  const [discountCodeItem, setDiscountCodeItem] = React.useState(null);
  const [couponItems, setCouponItems] = React.useState([]);

  const [repriced, setRepriced] = React.useState(null);

  const { post: checkDiscountCode } = useFetch(
    '/DiscountManagement/GetDiscount',
  );
  const { post: checkCoupon } = useFetch('/CouponManagement/CheckCoupon');
  const { get: repriceServices } = useFetch('/Services/RepriceServices');

  const getVisitType = () =>
    isPsdfMode
      ? 'PSDF'
      : isExtra
        ? 'Extra'
        : usingSecondary
          ? 'Secondary'
          : 'Main';

  function buildRepriceQuery(overrides = {}) {
    const params = new URLSearchParams();

    if (svc?.departmentExternalId != null) {
      params.set('departmentExternalId', String(svc.departmentExternalId));
    }

    if (svc?.serviceId != null) {
      params.set('serviceId', String(svc.serviceId));
    }

    // ✅ include insurance company
    const insurance = overrides.insurance ?? paymentInfo?.insurance ?? '';
    if (insurance) {
      params.set('insuranceCompany', insurance);
    }

    const addl =
      overrides.additionalServiceIds ??
      [...(additionalServices || []), ...(additionalSvc || [])]
        .map((a) => a?.serviceId)
        .filter((x) => x != null);
    addl.forEach((id) => params.append('additionalServiceIds', String(id)));

    const coupons = overrides.coupons ?? (couponItems || []).map((c) => c.id);
    coupons.forEach((id) => params.append('couponId', id));

    const discount = overrides.discountCode ?? discountCodeItem?.id;
    if (discount) params.set('discountCode', discount);

    params.set('visitType', getVisitType());
    params.set('IsIncorrectChild', IsIncorrectChild(userState, userInfo));
    params.set(
      'shouldRepriceAdditionalServices',
      (is3TWorkplace(service?.workplaceExternalId) && !isPaid) ||
        (!is3TWorkplace(service?.workplaceExternalId) &&
          !isMrt(service?.serviceTypeExternalId) &&
          isPaid),
    );
    return `?${params.toString()}`;
  }

  function normalizeDto(dto) {
    if (!dto || typeof dto !== 'object') {
      return {
        servicePrice: 0,
        additionalServicePrice: 0,
        total: 0,
        usedCoupons: [],
      };
    }
    const servicePrice =
      dto.servicePrice ?? dto.ServicePrice ?? dto.TotalPrice ?? 0;
    const additionalServicePrice =
      dto.additionalServicePrice ?? dto.AdditionalServicePrice ?? 0;
    const usedCoupons = dto.usedCoupons ?? dto.UsedCoupons ?? [];
    const total =
      dto.total ??
      dto.Total ??
      Number(servicePrice) + Number(additionalServicePrice);
    return {
      servicePrice: Number(servicePrice) || 0,
      additionalServicePrice: Number(additionalServicePrice) || 0,
      total: Number(total) || 0,
      usedCoupons,
    };
  }

  async function repriceNow(overrides) {
    try {
      const raw = await repriceServices(buildRepriceQuery(overrides));
      const dto = normalizeDto(raw);
      setRepriced(dto);

      // Sync coupon display to show actual deduction: "- €X.XX"
      const usedMap = new Map(
        (dto.usedCoupons || []).map((u) => [
          String(u.couponNumber),
          Number(u.amountUsed) || 0,
        ]),
      );
      setCouponItems((prev) =>
        (prev || []).map((it) => ({
          ...it,
          rightText: usedMap.has(it.id)
            ? `- ${formatPrice(usedMap.get(it.id))}`
            : '',
        })),
      );

      // Persist for registration submission
      setPaymentInfo((prev) => ({
        ...(prev || {}),
        couponsApplied: dto.usedCoupons || [],
        couponsAppliedTotal: (dto.usedCoupons || []).reduce(
          (s, u) => s + (Number(u.amountUsed) || 0),
          0,
        ),
      }));

      return { ok: true, dto };
    } catch {
      return { ok: false, errorText: 'Nepavyko perskaičiuoti kainos' };
    }
  }

  // ✅ single-call guard for auto reprice (prevents duplicates)
  const lastRepriceKeyRef = React.useRef('');

  React.useEffect(() => {
    // Always reprice (even in PSDF) so we get usedCoupons and correct totals
    if (!svc?.serviceId || !svc?.departmentExternalId) return;

    // Build a stable key for this state to avoid duplicate calls
    const repriceKey = JSON.stringify({
      dept: svc?.departmentExternalId,
      svc: svc?.serviceId,
      vt: getVisitType(),
      insurance: paymentInfo?.insurance || '',
      addl: [...(additionalServices || []), ...(additionalSvc || [])].map(
        (a) => a?.serviceId || null,
      ),
      coupons: (couponItems || []).map((c) => c.id),
      discount: discountCodeItem?.id || null,
      psdf: isPsdfMode,
      extra: isExtraMode,
    });

    if (repriceKey === lastRepriceKeyRef.current) return;
    lastRepriceKeyRef.current = repriceKey;

    repriceNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPsdfMode,
    svc?.departmentExternalId,
    svc?.serviceId,
    getVisitType(),
    JSON.stringify(additionalServices?.map((a) => a?.serviceId || null) || []),
    JSON.stringify(additionalSvc?.map((a) => a?.serviceId || null) || []),
    discountCodeItem?.id,
    JSON.stringify(couponItems?.map((c) => c.id) || []),
    paymentInfo?.insurance, // ✅ add insurance as reprice trigger
  ]);

  // ---- Discount code ----
  async function onApplyDiscount(code) {
    const trimmed = (code || '').trim();
    if (!trimmed) return { ok: false, errorText: 'Įveskite kodą' };
    if (!svc?.serviceId)
      return { ok: false, errorText: 'Paslauga nepasirinkta' };
    if (discountCodeItem?.id === trimmed) return { ok: true };

    try {
      const res = await checkDiscountCode({
        discountCode: trimmed,
        serviceId: svc.serviceId,
        departmentId: svc.departmentId,
        visitType: getVisitType(),
      });

      if (res?.status !== DiscountApplicationStatus.Undefined) {
        if (
          res?.status === DiscountApplicationStatus.NotFound ||
          res?.status === DiscountApplicationStatus.NotYetActive
        )
          return { ok: false, errorText: 'Nuolaidos kodas nerastas.' };
        if (res?.status === DiscountApplicationStatus.Expired)
          return { ok: false, errorText: 'Nuolaidos kodas nebegalioja.' };
        if (res?.status === DiscountApplicationStatus.NotApplicable)
          return {
            ok: false,
            errorText: 'Pasirinktai paslaugai nuolaida netaikoma.',
          };

        if (res?.status !== DiscountApplicationStatus.Applicable)
          return {
            ok: false,
            errorText: 'Nepavyko pritakyti nuolaidos kodo.',
          };
      }

      const rp = await repriceNow({ discountCode: trimmed });
      if (!rp.ok) return { ok: false, errorText: rp.errorText };

      const item = { id: trimmed, rightText: '' };
      setDiscountCodeItem(item);

      setPaymentInfo((prev) => ({
        ...prev,
        discountCodeNumber: trimmed,
      }));

      return { ok: true, item };
    } catch {
      return {
        ok: false,
        errorText: 'Įvyko klaida bandant pritaikyti nuolaidos kodą',
      };
    }
  }

  async function onRemoveDiscount(id) {
    const rp = await repriceNow({ discountCode: null });
    if (!rp.ok) return { ok: false, errorText: rp.errorText };

    if (discountCodeItem?.id === id) {
      setDiscountCodeItem(null);
      setPaymentInfo((prev) => ({
        ...prev,
        discountCodeNumber: '',
      }));
    }
    return { ok: true };
  }

  // ---- Coupon ----
  async function onApplyCoupon(code) {
    const trimmed = (code || '').trim();
    if (!trimmed) return { ok: false, errorText: 'Įveskite kodą' };
    if (!svc?.serviceId)
      return { ok: false, errorText: 'Paslauga nepasirinkta' };
    if (couponItems.some((x) => x.id === trimmed)) return { ok: true };

    try {
      const res = await checkCoupon({
        couponNumber: trimmed,
        serviceId: svc.serviceId,
        departmentId: svc.departmentExternalId,
        visitType: getVisitType(),
      });

      const norm = normalizeCouponResponse(res);
      if (!norm.found) return { ok: false, errorText: 'Kuponas nerastas' };
      if (!norm.dateOk) return { ok: false, errorText: 'Kuponas negalioja' };

      const nextCoupons = [...couponItems.map((c) => c.id), trimmed];
      const rp = await repriceNow({ coupons: nextCoupons });
      if (!rp.ok) return { ok: false, errorText: rp.errorText };

      // show actual deduction amount for the newly added coupon
      const used = (rp.dto.usedCoupons || []).find(
        (u) => String(u.couponNumber) === trimmed,
      );
      const rightText = used
        ? `- ${formatPrice(Number(used.amountUsed) || 0)}`
        : '';

      const item = { id: trimmed, rightText };
      setCouponItems((prev) => [...prev, item]);

      setPaymentInfo((prev) => ({
        ...prev,
        couponIds: nextCoupons,
      }));

      return { ok: true, item };
    } catch {
      return { ok: false, errorText: 'Įvyko klaida bandant pritaikyti kuponą' };
    }
  }

  async function onRemoveCoupon(id) {
    const nextCoupons = couponItems.filter((c) => c.id !== id).map((c) => c.id);
    const rp = await repriceNow({ coupons: nextCoupons });
    if (!rp.ok) return { ok: false, errorText: rp.errorText };

    // rebuild items with fresh deduction texts from DTO
    const usedMap = new Map(
      (rp.dto.usedCoupons || []).map((u) => [
        String(u.couponNumber),
        Number(u.amountUsed) || 0,
      ]),
    );
    setCouponItems(
      nextCoupons.map((cid) => ({
        id: cid,
        rightText: usedMap.has(cid) ? `- ${formatPrice(usedMap.get(cid))}` : '',
      })),
    );

    setPaymentInfo((prev) => ({
      ...prev,
      couponIds: nextCoupons,
    }));

    return { ok: true };
  }

  const displayLinePrice = repriced
    ? Number(repriced.servicePrice ?? 0)
    : linePrice;

  const displayAdditionals =
    repriced &&
    repriced.additionalServicePrice != null &&
    additionalServices.length > 0
      ? Number(repriced.additionalServicePrice)
      : additionalsTotal;

  const displayFooter =
    repriced && repriced.total != null
      ? Number(repriced.total)
      : displayLinePrice + displayAdditionals;

  const showAdditionals = Number(displayAdditionals) > 0;

  const baseTotalBeforeRedeems = Number(linePrice) + Number(additionalsTotal);
  const hasAnyRedeem = !!discountCodeItem || (couponItems?.length ?? 0) > 0;
  const showDiscountSections = baseTotalBeforeRedeems > 0 || hasAnyRedeem;
  const hasIncreasedPrice =
    svc?.serviceTypeExternalId == GSKServiceTypeExternalId ||
    svc?.serviceTypeExternalId == VGSKServiceTypeExternalId;
  React.useEffect(() => {
    setPaymentInfo((prev) => ({
      ...(prev || {}),
      computedTotal: Number(displayFooter) || 0,
    }));
  }, [displayFooter, setPaymentInfo]);

  return (
    <Card>
      <CardHeader>
        <Heading variant="3">Apmokėjimo informacija</Heading>
      </CardHeader>

      <CardContent>
        <div className="flex items-start gap-2 text-sm">
          <div>
            <CircleAlert className="w-5 h-5 text-primary" />
          </div>
          {hasIncreasedPrice ? (
            <span className="leading-5 text-primary font-primary">
              Konsultacijos metu gali būti reikalingos papildomos paslaugos,
              kurios neįskaičiuotos į pasirinktos paslaugos kainą:{' '}
              <a
                href="https://affidea.lt/lt/kainos/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Rodyti
              </a>
              .
            </span>
          ) : (
            <span className="leading-5 text-primary font-primary">
              Paslaugai suteikti gali būti reikalingos papildomos paslaugos.
              Visos kainos{' '}
              <a
                href="https://affidea.lt/lt/kainos/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                čia
              </a>
              .
            </span>
          )}
        </div>
        <div className="flex items-center justify-between mt-1">
          <div className="text-black">{title}</div>

          <div className="flex flex-wrap items-end justify-end gap-x-2 gap-y-0.5 text-primary font-bold text-right">
            {/* Current discounted/repriced price */}
            <span className="whitespace-nowrap text-primary font-bold text-right">
              {hasIncreasedPrice ? 'nuo ' : ''}
            </span>
            <span className="whitespace-nowrap">
              {formatPrice(displayLinePrice)}
            </span>

            {/* Base/original price */}
            {baseMainPrice && baseMainPrice > displayLinePrice && (
              <span className="text-gray-400 line-through font-normal whitespace-nowrap">
                {formatPrice(baseMainPrice)}
              </span>
            )}
          </div>
        </div>

        {svc?.serviceParts?.length > 0 && (
          <div className="mt-1 flex flex-col gap-0.5">
            {svc?.serviceParts.map((part, index) => {
              const hasObjectServices = part.objectServices?.length > 0;
              const displayText = hasObjectServices
                ? part.objectServices
                    .map((obj) =>
                      obj.name
                        .replace(' | AFF / MP', '')
                        .replace(' | AFF', '')
                        .replace(' | MP', ''),
                    )
                    .join(', ')
                : part.amount
                  ? `${part.name} - ${part.amount}`
                  : part.name;

              return (
                <span key={index} className="text-xs text-gray-400">
                  {displayText}
                </span>
              );
            })}
          </div>
        )}

        {showAdditionalLabServices && additionalSvc?.length > 0 && (
          <div className="mt-2">
            {/* Header row with title and total */}
            <div className="flex items-center justify-between mb-2">
              <div className="text-black font-semibold">
                Papildomi laboratoriniai tyrimai
              </div>
              <div className="text-primary font-bold">
                {isPsdfMode
                  ? formatPrice(0)
                  : formatPrice(additionalLabServiceTotal)}
              </div>
            </div>

            {/* List of additional services */}
            <div className="space-y-1">
              {additionalSvc.map((s, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="text-black flex">
                    <X
                      className="text-red-500 cursor-pointer"
                      onClick={() => {
                        setServices((prev) =>
                          prev.filter((service) => service !== s),
                        );
                      }}
                    />
                    {s.serviceTitle}
                  </div>
                  <div className="text-primary font-bold">
                    {formatPrice(
                      isPsdfMode ? 0 : s?.priceWithDiscount || s?.price,
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showAdditionals && (
          <div className="flex mt-2">
            <div className="text-black font-bold">
              Papildomos privalomos paslaugos:
            </div>
            {/* <div className="text-primary font-bold">
              {formatPrice(displayAdditionals - additionalLabServiceTotal)}
            </div> */}
          </div>
        )}

        {showAdditionals &&
          additionalServices &&
          additionalServices.length > 0 && (
            <div className="flex  justify-between flex-col mt-2">
              {additionalServices.map((service, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="text-black"> {service.name}</div>
                  <div className="text-primary font-bold">
                    {formatPrice(service.price)}
                  </div>
                </div>
              ))}
            </div>
          )}

        <Separator className="my-2" />

        {showDiscountSections && (
          <>
            <RedeemCodeSection
              singleUse
              title="Nuolaidos kodas"
              placeholder="Įveskite nuolaidos kodą"
              items={discountCodeItem ? [discountCodeItem] : []}
              onApply={onApplyDiscount}
              onRemove={onRemoveDiscount}
            />
            <Separator className="my-2" />

            <RedeemCodeSection
              title="Kuponai"
              placeholder="Įveskite kupono kodą"
              items={couponItems}
              onApply={onApplyCoupon}
              onRemove={onRemoveCoupon}
            />
            <Separator className="my-2" />
          </>
        )}
      </CardContent>

      <CardFooter className="p-0 px-2 sm:px-4 text-xl py-4">
        <div className="flex w-full items-center justify-between">
          <div className="text-black">Iš viso su PVM</div>
          <div className="flex flex-wrap items-end justify-end gap-x-2 gap-y-0.5 text-primary font-bold text-right">
            {/* Final total after discounts/coupons/repricing */}
            <span className="whitespace-nowrap">
              {formatPrice(displayFooter)}
            </span>

            {/* Base/original total */}
            {baseTotalPrice > displayFooter && (
              <span className="text-gray-400 line-through font-normal whitespace-nowrap">
                {formatPrice(baseTotalPrice)}
              </span>
            )}
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}
